import { animate, style, transition, trigger } from '@angular/animations';
import { Component, ComponentFactoryResolver, Directive, Inject, OnInit, Type, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Action, actionColor, ENVIRONMENT, findInMenuItems, MenuItem, MenuStorage } from '@scisys-uim/uim-common';
import { LandingPageHostDirective, LandingPageTitleDirective } from './landing-page-host.directive';


export const fadeInOutTimeout = 250;
export const fadeInOut = trigger('fadeInOut', [
  transition('void => *', [style({ opacity: '0' }), animate(fadeInOutTimeout)]),
  transition('* => void', [animate(fadeInOutTimeout, style({ opacity: '0' }))]),
  transition('* => *', [
    style({ opacity: '0' }),
    animate(fadeInOutTimeout, style({ opacity: '1' })),
  ]),
]);


export interface LandingPageContentComponent {
  landingPageConfig: LandingPageConfig;

}

export interface LandingPageConfig {
  componentType: Type<LandingPageContentComponent>;
  titleText?: string;
  menuItem?: MenuItem;
  urlForMatchingMenuItem?: string;
}

@Component({
  selector: 'app-landing-page',
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.scss'],
  animations: [fadeInOut]
})
export class LandingPageComponent implements OnInit {

  @ViewChild(LandingPageHostDirective, { static: true }) host: LandingPageHostDirective;

  private _bannerImageAssetPath: string;
  private _landingPageEnvironmentOverrides: any;
  contentComponent: LandingPageContentComponent;
  protected config: LandingPageConfig;
  openedMenu: MenuItem = null;
  openedSubMenus: MenuItem[] = [];



  openSubMenu(parent: MenuItem) {
    this.openedSubMenus.push(parent);
  }

  closeSubMenu() {
    this.openedSubMenus.pop();
  }

  constructor(@Inject(ENVIRONMENT) private environment: any, private componentFactoryResolver: ComponentFactoryResolver, private route: ActivatedRoute, private menuStorage: MenuStorage) {
  }
  get bannerImageAssetPath(): string {
    if (this._bannerImageAssetPath == null && this.componentTypeName != null) {
      this._bannerImageAssetPath = this.landingPageEnvironmentOverrides?.bannerImageAssetPath
        || this.environment?.bannerImageAssetPathDefault;
    }
    return this._bannerImageAssetPath;
  }

  get componentTypeName(): string {
    return this.config?.componentType?.name;
  }

  get landingPageEnvironmentOverrides(): any {
    if (!this._landingPageEnvironmentOverrides && this.componentTypeName != null) {
      var environmentOverrideLandingPagesConfig = this.environment?.landingPages || {};
      this._landingPageEnvironmentOverrides = environmentOverrideLandingPagesConfig[this.componentTypeName] || {};
    }
    return this._landingPageEnvironmentOverrides;
  }

  get titleText(): string {
    return this.config ? this.config.titleText : null;
  }

  get currentMenuParent(): MenuItem {
    if (this.openedSubMenus.length == 0) {
      return this.config?.menuItem;
    }
    return this.openedSubMenus[this.openedSubMenus.length - 1];
  }
  get previousMenuParent(): MenuItem {
    if (this.openedSubMenus.length == 0) {
      return null;
    }
    if (this.openedSubMenus.length == 1) {
      return this.config?.menuItem;
    }
    return this.openedSubMenus[this.openedSubMenus.length - 2];
  }

  get menuItemChildren(): MenuItem[] {
    return this.currentMenuParent?.children;
  }

  get menutItemChildrenFiltered(): MenuItem[] {
    if (this.menuItemChildren) {
      return this.menuItemChildren.filter((menuItem) => !this.isNoLinkEmptyContainerMenuItem(menuItem));
    }
    return null;
  }

  getMenutItemChildrenFiltered(parent: MenuItem): MenuItem[] {
    if (parent?.children) {
      return parent.children.filter((child) => !this.isNoLinkEmptyContainerMenuItem(child));
    }
    return null;
  }

  get menuItemLeafLinkChildren(): MenuItem[] {
    if (this.menuItemChildren == null) {
      return null;
    }
    return this.menuItemChildren.filter(child => this.isLeafMenuItem(child));
  }

  get menuItemContainerChildren(): MenuItem[] {
    if (this.menuItemChildren == null) {
      return null;
    }
    return this.menuItemChildren.filter(child => this.isContainerMenuItem(child));
  }

  isLeafMenuItem(item: MenuItem): boolean {
    return item.link.link != null && (item.children == null || item.children.length == 0);
  }

  isContainerMenuItem(item: MenuItem): boolean {
    return item.children && item.children.length > 0;
  }

  isNoLinkEmptyContainerMenuItem(item: MenuItem): boolean {
    return (item.children == null || (item.children && item.children.length <= 0)) && item.link.link == null
  }

  isNoLinkContainerMenuItem(item: MenuItem): boolean {
    return item.type == null && item.children && item.children.length > 0;
  }

  isLinkContainerMenuItem(item: MenuItem): boolean {
    return item.link.link != null && item.children && item.children.length > 0;
  }

  getContainerChildren(item: MenuItem): MenuItem[] {
    return item?.children;//.filter(child => this.isLeafMenuItem(child))
  }


  isOpenMenu(menu: MenuItem, mm) {
    return this.openedMenu == menu;
  }

  menuOpened(event, menu: MenuItem) {
    this.openedMenu = menu;
  }

  menuClosed(event, menu: MenuItem) {
    this.openedMenu = null;
  }

  isOpenSubMenu(menu: MenuItem, mm) {
    return this.hasOpenedSubMenu() && this.currentMenuParent == menu;
  }

  hasOpenedSubMenu() {
    return this.openedSubMenus.length > 0;
  }

  subMenuOpened(event, menu: MenuItem) {
  }

  subMenuClosed(event, menu: MenuItem) {
  }

  ngOnInit(): void {
    if (this.host) {
      const viewContainerRef = this.host.viewContainerRef;
      if (viewContainerRef) {
        this.route.data.subscribe((config: LandingPageConfig) => {
          this.config = config;
          viewContainerRef.clear();
          if (this.config.componentType) {
            const componentFactory = this.componentFactoryResolver.resolveComponentFactory(this.config.componentType);
            const componentRef = viewContainerRef.createComponent(componentFactory);
            this.contentComponent = <LandingPageContentComponent>componentRef.instance;
            this.contentComponent.landingPageConfig = this.config;
          }


          this.menuStorage.itemChanges().subscribe((items: MenuItem[]) => {
            const url = this.urlForMatchingMenuItem();
            const found = findInMenuItems(items, url)
            if (found) {
              config.menuItem = found;
            }
          });
        });
      }
    }
  }

  ngAfterViewInit() {
  }

  protected urlForMatchingMenuItem() {
    if (this.config.urlForMatchingMenuItem) {
      return this.config.urlForMatchingMenuItem;
    }
    return this.route.snapshot.url.join('/');
  }
}



@Directive()
export abstract class AbstractLandingPageContentComponent implements LandingPageContentComponent, OnInit {

  @ViewChild(LandingPageTitleDirective, { static: true }) titleComponent: LandingPageTitleDirective;

  landingPageConfig: LandingPageConfig;

  ngOnInit(): void {
    if (this.titleComponent) {
      const nativeElement = this.titleComponent.viewContainerRef.element.nativeElement;
      if (nativeElement) {
        if (nativeElement.innerText) {
          this.landingPageConfig.titleText = nativeElement.innerText || this.landingPageConfig.titleText;
          nativeElement.remove();
        }
      }
    }
  }

  toActionColor(val: Action | string | boolean): string {
    return actionColor(val);
  }
}
